/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 * 
 ********************************************************************!*/

import { Edge, Elements, removeElements } from "@braks/vue-flow";
import { IBlock, IButtonsElement, ICondition, IDelay, ISplit, IStepFlow } from "@chatcaptain/types/chatbot";
import { store } from "@/store";
import { BotStore } from "@/store/constants";

export const getEdges = (blocks: IBlock[]): Edge[] => {
  const edges: Edge[] = [];
  blocks.forEach(block => {
    connectStep(block, edges);
  });
  return edges;
}

export const connectStep = (block: IBlock, elements: Elements): Elements => {
  if(block.id == "entry" || (block.type && block.type == "intent")) {
    connectStartAnchor(block, elements);
  } else if(!block.type || block.type == "elements"){
    connectBlock(block, elements);
  } else if(block.type == "delay"){
    connectDelay(block as IDelay, elements);
  } else if(block.type == "flow"){
    connectFlow(block as IStepFlow, elements);
  } else if(block.type == "split"){
    connectSplit(block as ISplit, elements);
  } else if(block.type == "condition"){
    connectCondition(block as ICondition, elements);
  }
  return elements;
}

export const connectStartAnchor = (data: IBlock, elements: Elements): Elements => {
  if(data.start && data.start.trim().length > 0){
    elements.push(toEdge(data.id, data.start));
  }
  return elements;
}

export const connectBlock = (data: IBlock, elements: Elements): Elements => {
  if(!data.type || data.type == "elements"){
    if(data.elements){
      const buttonsElement = data.elements?.filter(el => el.type == "buttons");
      if(buttonsElement && buttonsElement.length > 0){
        const buttons = buttonsElement[0] as IButtonsElement;
        buttons.content.forEach((button, index) => {
          const name = typeof button.name == "string" ? button.name : button.name[store.getters[BotStore.Getters.GET_DEFAULT_LANGUAGE]];
          if(button.type == "block" && button.value) elements.push(toEdge(data.id, button.value, name, index));
        });
      }
    }
    if(data.user_input && data.user_input.target && data.user_input.target.trim().length > 0){
      elements.push(toEdge(data.id, data.user_input.target, "Nutzereingabe", "user_input"));
    }
    if(data.redirect && data.redirect.trim().length > 0) elements.push(toEdge(data.id, data.redirect));
  }
  return elements;
}

export const connectDelay = (data: IDelay, elements: Elements): Elements => {
  if(data.redirect && data.redirect.trim().length > 0){
    let time = "";
    const seconds = data.delay || 0;
    const d = Math.floor(seconds / (3600*24));
    const h = Math.floor(seconds % (3600*24) / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = Math.floor(seconds % 60);
    time = ("0" + m).slice(-2) + ":" + ("0" + s).slice(-2);
    if(h > 0) time = ("0" + h).slice(-2) + ":" + time;
    if(d > 0) time = ("0" + d).slice(-2) + ":" + time;
    elements.push(toEdge(data.id, data.redirect, time));
  }
  return elements;
}

export const connectSplit = (data: ISplit, elements: Elements): Elements => {
  if(data.redirects){
    Object.keys(data.redirects).forEach(key => {
      const redirect = data.redirects[key];
      if(redirect && redirect.trim().length > 0) elements.push(toEdge(data.id, redirect, key + "(" + (data.split[key] ? Math.round(data.split[key] * 100) : 50) + " %)", key));
    });
  }
  return elements;
}

export const connectCondition = (data: ICondition, elements: Elements): Elements => {
  if(data.redirects){
    Object.keys(data.redirects).forEach(key => {
      const redirect = data.redirects[key];
      if(redirect && redirect.trim().length > 0) elements.push(toEdge(data.id, redirect, (key == "T" ? "Wahr" : "Falsch"), key));
    });
  }
  return elements;
}

export const connectFlow = (data: IStepFlow, elements: Elements): Elements => {
  if(data.outlets){
    Object.keys(data.outlets).forEach(outletId => {
      if(data.outlets) {
        const outlet = data.outlets[outletId];
        if(outlet.connected) elements.push(toEdge(data.id, outlet.connected, outlet.name, outletId));
      }
    });
  }
  return elements;
}

export const removeEdgeBySource = (nodes: Elements, sourceId: string, index?: number | string) => {
  const edges = nodes.filter(element => {
    const el = element as Edge;
    return el.source && el.source == sourceId && (!index && index !== 0 || el.sourceHandle == index.toString());
  });
  return edges.length > 0 ? removeElements(edges, nodes) : nodes;
}

export const toEdge = (sourceId?: string, targetId?: string, label?: string, sourceIndex?: number | string): Edge => {
  if(sourceId) sourceId = sourceId.split("_")[0];
  if(sourceId && sourceId.match("/")) sourceId = sourceId.split("/")[1];
  if(!sourceId) sourceId = randomId();
  if(!targetId) targetId = randomId();
  if(targetId && targetId.match("/")) targetId = targetId.split("/")[1];
  const edge = {
    id: sourceId + (sourceIndex || sourceIndex === 0 ? sourceIndex : "") + '-' + targetId,
    source: sourceId,
    target: targetId,
    sourceHandle: (sourceIndex || sourceIndex === 0) ? sourceIndex.toString() : "default",
    class: "flow-edge",
    animated: false,
    arrowHeadType: "arrowclosed"
  } as Edge;
  if(label) edge.label = label;
  return edge;
}

const randomId = (): string => {
  return Math.random().toString(36).slice(4);
}